import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Globe, Loader2 } from "lucide-react";
import CountryPicker from "@/components/CountryPicker";

/**
 * First-sign-in country prompt.
 *
 * Renders an unclosable overlay for any signed-in user who doesn't yet have
 * `country_code` set on their profile. The only path that currently lands
 * here is Google OAuth signups (the email register form collects the
 * country up-front).
 *
 * On submit it calls PUT /api/auth/country and refreshes the cached `user`.
 * Backdrop is not dismissable — users must pick a country to continue.
 */
export default function CountrySetupModal() {
  const { user, setUser } = useAuth();
  const [countryCode, setCountryCode] = useState("JM");
  const [saving, setSaving] = useState(false);

  // Detect missing country_code. Users who already have one set don't see
  // the modal (back-compat — pre-multi-country users were backfilled to JM
  // on server startup).
  const needsCountry =
    !!user &&
    !user.country_code &&
    user.auth_provider === "google";

  // Sync local state to user (in case it's preset from a prior session).
  useEffect(() => {
    if (user?.country_code) setCountryCode(user.country_code);
  }, [user]);

  if (!needsCountry) return null;

  const handleSubmit = async () => {
    if (!countryCode) {
      toast.error("Please select your country");
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/auth/country`,
        { country_code: countryCode },
        { withCredentials: true }
      );
      // Refresh the user so the modal vanishes.
      try {
        const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(me.data);
      } catch {
        // If /auth/me hiccups, optimistically set so we don't loop.
        setUser({ ...user, country_code: countryCode });
      }
      toast.success("Welcome to TalaDraw!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save country");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center px-6"
      data-testid="country-setup-modal"
    >
      <div className="w-full max-w-sm bg-[#0c0c0e] border border-zinc-800 rounded-2xl p-6 shadow-2xl">
        <div className="flex flex-col items-center text-center mb-5">
          <div className="w-12 h-12 rounded-full bg-[#00FF94]/10 flex items-center justify-center mb-3">
            <Globe className="w-6 h-6 text-[#00FF94]" strokeWidth={1.5} />
          </div>
          <h2 className="font-heading text-lg font-bold text-white mb-1">
            Where do you live?
          </h2>
          <p className="text-zinc-400 text-xs leading-relaxed">
            We&apos;ll show you ATMs in your country and tailor your map view. You can
            change this any time in Profile.
          </p>
        </div>

        <CountryPicker
          value={countryCode}
          onChange={setCountryCode}
          size="md"
          testId="country-setup-picker"
          className="mb-4"
        />

        <button
          onClick={handleSubmit}
          disabled={saving || !countryCode}
          className="w-full neon-btn py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="country-setup-save-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            "Continue"
          )}
        </button>
      </div>
    </div>
  );
}
